/**
 * Silo v2/v3 public-data fetcher via the Silo GraphQL indexer.
 *
 * Endpoint: `https://api-v3.silo.finance` — serves BOTH Silo v2 and v3 silos.
 * Indexed chains at time of writing: Ethereum (1), Arbitrum (42161),
 * Avalanche (43114), XDC (50), Injective (1776). Sonic (146) is NOT indexed
 * by this API and must still use the on-chain path.
 *
 * The `market.{borrowRate,depositRate,utilization,supply,borrowed}` fields
 * are returned as decimal strings (already in token units, not wei), so the
 * converter consumes them directly without any on-chain decimal shifting.
 *
 * `solvencyOracle.quote` / `maxLtvOracle.quote` are decimal strings in the
 * oracle's quote-token denomination — these feed the narrowed oracle-price
 * prefetch that short-circuits the on-chain Silo oracle multicall.
 */
const BASE_URL = 'https://api-v3.silo.finance'

/**
 * Chains for which the Silo GraphQL indexer has data. For any chain not in
 * this set, the caller must fall back to the on-chain path.
 */
export const SILO_API_SUPPORTED_CHAIN_IDS = new Set<string>([
  '1', // Ethereum
  '42161', // Arbitrum
  '43114', // Avalanche
  '50', // XDC
  '1776', // Injective
])

export type SiloApiMarket = {
  index: number
  inputTokenId: string
  inputToken?: {
    id: string
    symbol: string
    decimals: number
  } | null
  lt: string
  maxLtv: string
  liquidationTargetLtv?: string | null
  daoFee?: string | null
  deployerFee?: string | null
  liquidationFee?: string | null
  flashLoanFee?: string | null
  borrowRate: string
  depositRate: string
  supply: string
  borrowed: string
  supplyUsd: number | null
  borrowedUsd: number | null
  liquidityUsd?: number | null
  utilization: string
  // Collateral-only counterparts to `supply` / `supplyUsd`. The indexer's
  // `supply` field aggregates Collateral + Protected — only Collateral
  // backs borrows, so utilization / liquidity math must use these.
  collateralAssetsSupply?: string | null
  collateralAssetsSupplyUsd?: number | null
  liquidity?: string | null
  solvencyOracleAddress?: string | null
  maxLtvOracleAddress?: string | null
  interestRateModelId?: string | null
  sTokenId?: string | null
  spTokenId?: string | null
  dTokenId?: string | null
  solvencyOracle?: {
    id: string
    quote: string
    quoteTimestamp: string
    baseTokenId: string
    quoteTokenId: string
  } | null
  maxLtvOracle?: {
    id: string
    quote: string
    quoteTimestamp: string
  } | null
}

export type SiloApiEntry = {
  id: string
  chainId: number
  configAddress: string
  name?: string | null
  protocol?: {
    protocolVersion: string
  } | null
  market1: SiloApiMarket
  market2: SiloApiMarket
}

const query = (chainId: string, limit: number) => `
query GetSilos {
  silos(where: {chainId_in: [${chainId}]}, limit: ${limit}) {
    items {
      id
      chainId
      configAddress
      name
      protocol {
        protocolVersion
      }
      market1 {
        index
        inputTokenId
        inputToken { id symbol decimals }
        lt
        maxLtv
        liquidationTargetLtv
        daoFee
        deployerFee
        liquidationFee
        flashLoanFee
        borrowRate
        depositRate
        supply
        borrowed
        supplyUsd
        borrowedUsd
        liquidityUsd
        utilization
        collateralAssetsSupply
        collateralAssetsSupplyUsd
        liquidity
        solvencyOracleAddress
        maxLtvOracleAddress
        interestRateModelId
        sTokenId
        spTokenId
        dTokenId
        solvencyOracle {
          id
          quote
          quoteTimestamp
          baseTokenId
          quoteTokenId
        }
        maxLtvOracle {
          id
          quote
          quoteTimestamp
        }
      }
      market2 {
        index
        inputTokenId
        inputToken { id symbol decimals }
        lt
        maxLtv
        liquidationTargetLtv
        daoFee
        deployerFee
        liquidationFee
        flashLoanFee
        borrowRate
        depositRate
        supply
        borrowed
        supplyUsd
        borrowedUsd
        liquidityUsd
        utilization
        collateralAssetsSupply
        collateralAssetsSupplyUsd
        liquidity
        solvencyOracleAddress
        maxLtvOracleAddress
        interestRateModelId
        sTokenId
        spTokenId
        dTokenId
        solvencyOracle {
          id
          quote
          quoteTimestamp
          baseTokenId
          quoteTokenId
        }
        maxLtvOracle {
          id
          quote
          quoteTimestamp
        }
      }
    }
  }
}
`

const liteQuery = (chainId: string, limit: number) => `
query GetSilosLite {
  silos(where: {chainId_in: [${chainId}]}, limit: ${limit}) {
    items {
      configAddress
      protocol { protocolVersion }
      market1 {
        index
        inputTokenId
        supply
        borrowed
        supplyUsd
        borrowedUsd
        solvencyOracleAddress
        solvencyOracle { quote quoteTokenId }
      }
      market2 {
        index
        inputTokenId
        supply
        borrowed
        supplyUsd
        borrowedUsd
        solvencyOracleAddress
        solvencyOracle { quote quoteTokenId }
      }
    }
  }
}
`

/**
 * Lightweight Silo GraphQL fetch that requests ONLY the fields the
 * oracle-price prefetch parser consumes (`siloV{2,3}Graphql.ts`).
 *
 * Same endpoint and filter semantics as `fetchSiloMarkets`, but the trimmed
 * payload is small enough to avoid the per-IP rate-limiting the full query
 * triggers when run frequently across multiple chains.
 */
export async function fetchSiloMarketsLite(
  chainId: string,
  options?: {
    protocolVersion?: 'v2' | 'v3'
    limit?: number
  },
): Promise<SiloApiEntry[]> {
  if (!SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)) return []

  const limit = options?.limit ?? 500
  const response = await fetch(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: liteQuery(chainId, limit) }),
  })

  if (!response.ok) {
    throw new Error(
      `[silo-api] Network error: ${response.status} - ${response.statusText}`,
    )
  }

  const payload: any = await response.json()
  if (payload?.errors?.length) {
    throw new Error(
      `[silo-api] GraphQL error: ${payload.errors
        .map((e: any) => e?.message)
        .join('; ')}`,
    )
  }

  const items: SiloApiEntry[] = payload?.data?.silos?.items ?? []
  if (!options?.protocolVersion) return items
  return items.filter(
    (s) => s.protocol?.protocolVersion === options.protocolVersion,
  )
}

/**
 * Fetch all Silo v2/v3 silos for a single chain from the GraphQL indexer.
 *
 * Returns the raw `silos.items` list (optionally filtered to a specific
 * protocol version). Throws on HTTP / GraphQL errors so the caller can
 * distinguish "API failed → fall back to RPC" from "API returned nothing".
 */
export async function fetchSiloMarkets(
  chainId: string,
  options?: {
    protocolVersion?: 'v2' | 'v3'
    limit?: number
  },
): Promise<SiloApiEntry[]> {
  if (!SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)) return []

  const limit = options?.limit ?? 500
  const response = await fetch(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: query(chainId, limit) }),
  })

  if (!response.ok) {
    throw new Error(
      `[silo-api] Network error: ${response.status} - ${response.statusText}`,
    )
  }

  const payload: any = await response.json()
  if (payload?.errors?.length) {
    throw new Error(
      `[silo-api] GraphQL error: ${payload.errors
        .map((e: any) => e?.message)
        .join('; ')}`,
    )
  }

  const items: SiloApiEntry[] = payload?.data?.silos?.items ?? []
  if (!options?.protocolVersion) return items
  return items.filter(
    (s) => s.protocol?.protocolVersion === options.protocolVersion,
  )
}
